<template>
    <v-dialog v-model="videoListDialog" no-click-animation width="90%" max-width="900">
        <v-card component="ProjectVideoListDialog">
            <div class="holder">
                <btn class="close" size="icon" color="transparent" :icon="'close'" @click="close" />
                <div class="video">
                    <video ref="video" preload="metadata" width="100%" :key="`video-selected-${video_key}`" controls>
                        <source :src="video_url" type="video/mp4" />
                    </video>
                </div>
                <div class="list">
                    <h3>Videos</h3>
                    <div class="scroller">
                        <project-video-list-dialog-item
                            v-for="(video, index) in videos"
                            :key="video.id"
                            :index="`${index + 1}.`"
                            :video="video"
                            :class="{ selected: selectedItem.id === video.id, uploaded: video.converted_at }"
                            @play="loadSelectedVideo"
                            @remove="removeSelectedVideo"
                            @download="downloadSelectedVideo"
                        />
                    </div>
                    <footer>
                        <div class="buttons" v-if="isAdmin">
                            <video-gallery-upload :uploading="[states.uploading].includes(state)" @submit="onUpload" />
                        </div>
                    </footer>
                </div>
            </div>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import draggable from 'vuedraggable';
import '@/utils/moveableDialog.js';
import Mp3Player from '@/components/.global/Mp3Player.vue';
import ProjectVideoListDialogItem from '@/components/Project/Dialogs/ProjectVideoListDialog/ProjectVideoListDialogItem';
import VideoGalleryUpload from '@/components/Profile/Video/Gallery/VideoGalleryUpload.vue';

const states = {
    idle: 'idle',
    uploading: 'uploading',
    error: 'error',
};

export default {
    name: 'ProjectVideoListDialog',

    props: {
        emailRequest: {
            type: Object,
            videos: [],
        },
    },

    data() {
        return {
            video_url: null,
            video_key: 0,
            selectedItem: null,

            states,
            state: states.idle,
            error: null,
            poll: null,
        };
    },

    beforeDestroy() {
        this.$_eventsUnsubscribe();
        this.stopPolling();
    },

    methods: {
        ...mapActions([
            'loadVideo',
            'removeVideoRequestVideo',
            'storeVideoRequestVideo',
            'loadActorVideoRequests',
            'loadProject',
        ]),
        ...mapMutations(['setProjectVideoListDialog']),

        async loadSelectedVideo(video) {
            this.video_url = this.getVideoRequestVideoFileUrl(video.hash);
            this.video_key++;
            this.selectedItem = video;
        },

        async removeSelectedVideo(video) {
            await this.removeVideoRequestVideo(video);
            this.$_eventsDispatch('videoRequestVideoRemoved', video);
        },

        async downloadSelectedVideo(video, index) {
            window.open(`${this.getVideoRequestVideoFileUrl(video.hash)}/download?name_prefix=${parseInt(index)}_`);
        },

        close() {
            this.$refs.video.pause();
            this.video_url = null;
            this.setProjectVideoListDialog({ visible: false, emailRequest: null });
            this.poll = null;
        },

        async onUpload(file) {
            this.stopPolling();

            this.$_setState(states.uploading);

            try {
                const formData = new FormData();

                formData.append('video_hash', this.getUserInfo().hash);
                formData.append('video', file, file.name);
                formData.append('uploaded', file.name);
                formData.append('avoidConvertio', !!this.isAdmin);

                await this.storeVideoRequestVideo(formData);
                await this.$_eventsDispatch('videoRequestVideoUploaded', file);

                this.startPolling(true);
            } catch (e) {
                this.$_setState(states.error, states.idle);
                this.$env.log.error(e);
                this.$bugsnag.notify(e);
            } finally {
                this.$_setState(states.idle);
            }
        },

        getUserInfo() {
            return this.getProjectVideoListDialog.emailRequest ?? false;
        },

        startPolling(force = false) {
            const poll = async () => {
                if (!this.hasUnprocessedVideos) {
                    return;
                }

                this.$_eventsDispatch('videoRequestVideoUploaded', true);

                this.poll = setTimeout(poll, 5000);
            };

            if (this.hasUnprocessedVideos || force) {
                this.poll = setTimeout(poll, 1000);
            }
        },

        stopPolling() {
            if (this.poll) {
                clearTimeout(this.poll);
            }
        },
    },

    computed: {
        ...mapGetters([
            'getProjectVideoListDialog',
            'getVideoRequestVideoFileUrl',
            'getIsLoggedIn',
            'hasRole',
            'getUser',
            'getProject',
        ]),

        videoListDialog: {
            get: function () {
                if (this.hasUnprocessedVideos && !this.poll) {
                    this.startPolling();
                }

                return this.getProjectVideoListDialog.visible;
            },
            set: function (val) {
                if (!val) {
                    this.close();
                }
            },
        },

        videos() {
            return this.getProjectVideoListDialog.emailRequest?.videos ?? [];
        },

        isAdmin() {
            return (
                this.getIsLoggedIn && (this.hasRole('role_administrator') || this.getUser?.isImpersonated() || false)
            );
        },

        hasUnprocessedVideos() {
            return this.videos.some((i) => !i.converted_at);
        },
    },

    watch: {
        videoListDialog(val) {
            if (val) {
                const [video] = this.videos;
                if (!video) return;
                this.loadSelectedVideo(video);
                this.selectedItem = video;
            }
        },
    },

    components: {
        ProjectVideoListDialogItem,
        draggable,
        Mp3Player,
        VideoGalleryUpload,
    },
};
</script>

<style lang="less" scoped>
@import '~@variables';

[component='ProjectVideoListDialog'] {
    .close {
        position: absolute;
        top: 0;
        right: 0;
        margin: 0;
    }

    .holder {
        margin: 0;
        min-height: 466px;
        max-height: 90vh;
        display: flex;

        .video {
            width: 70%;

            video {
                width: 100%;
                height: 100%;
                background: @black;
            }
        }

        .list {
            width: 30%;
            display: flex;
            flex-direction: column;

            h3 {
                margin: 15px;
            }

            .scroller {
                .flex-column;
                overflow-y: auto;
                width: 100%;
                height: 200px;
                flex-grow: 1;
            }
        }
    }

    footer {
        padding: 20px;
        border-top: solid 2px #eee;

        .buttons {
            display: flex;
            flex-direction: row;
            justify-content: center;
        }
    }
}
</style>
